import { useEffect, useRef, useState, type RefObject, type LegacyRef } from 'react'
import { Send, ListTodo, Search, Zap, BarChart3, Paperclip, X } from 'lucide-react'
import { TestOpsCommandLogo } from './TestOpsCommandLogo'
import { getFileIcon, formatSize } from './ChatInput'
import clsx from 'clsx'
import type { Integration, ActionItem } from '../external-types'
import { PortfolioDropdown } from './PortfolioDropdown'
import { IntegrationsDropdown } from './IntegrationsDropdown'

// ---------------------------------------------------------------------------
// Capability cards — grouped by what the user can *do*, not by provider
// ---------------------------------------------------------------------------

interface CapabilityCard {
  icon: typeof Search
  title: string
  description: string
  gradient: string
  iconBg: string
  iconColor: string
  borderHover: string
  prompts: { text: string; providers?: string[] }[]
}

const CAPABILITIES: CapabilityCard[] = [
  {
    icon: Search,
    title: 'Investigate',
    description: 'Cross-reference test runs, tickets, code, and docs in one place',
    gradient: 'from-info-soft to-info-soft',
    iconBg: 'bg-info-soft',
    iconColor: 'text-info-foreground',
    borderHover: 'hover:border-info/40',
    prompts: [
      // Cross-product first — the hero example for demos
      { text: 'Show failing tests from last week and link related Jira tickets', providers: ['jira'] },
      { text: 'Find open bugs assigned to me and which tests cover them', providers: ['jira'] },
      { text: 'Show open PRs in my repo and any failing tests in their branches', providers: ['github', 'gitlab'] },
      { text: 'Search the docs for the auth flow and which tests verify it', providers: ['confluence'] },
      { text: 'List all tables in the database and find tests that touch them', providers: ['postgres'] },
      { text: 'Show me my test cases tagged regression with their last 5 runs' },
    ],
  },
  {
    icon: Zap,
    title: 'Take Action',
    description: 'Create tickets, run tests, update statuses across connected tools',
    gradient: 'from-warning-soft to-warning-soft',
    iconBg: 'bg-warning-soft',
    iconColor: 'text-warning-foreground',
    borderHover: 'hover:border-warning/40',
    prompts: [
      // High-impact cross-product action up top
      { text: 'For each failing test in the last 24h, file a Jira bug with steps and screenshot', providers: ['jira'] },
      { text: 'Create a GitHub issue for the flaky checkout test with last-failure context', providers: ['github'] },
      { text: 'Comment on the latest PR with the test impact summary', providers: ['github', 'gitlab'] },
      { text: 'Run the smoke suite against the QA environment and post the result' },
      { text: 'Generate a regression test for the login flow' },
    ],
  },
  {
    icon: BarChart3,
    title: 'Get Insights',
    description: 'Sprint health, test trends, deploy readiness — synthesised from your stack',
    gradient: 'from-success-soft to-success-soft',
    iconBg: 'bg-success-soft',
    iconColor: 'text-success-foreground',
    borderHover: 'hover:border-success/40',
    prompts: [
      // Composed answer that touches multiple sources
      { text: 'Compare this sprint’s test pass rate to last sprint and call out regressions', providers: ['jira'] },
      { text: 'Which tests are blocking the next release?', providers: ['jira'] },
      { text: 'Show recent commits on main with their test impact', providers: ['github', 'gitlab'] },
      { text: 'Status of recent Jenkins builds, grouped by failure type', providers: ['jenkins'] },
      { text: 'Summarise my last 7 days of test runs and where I’m losing time' },
    ],
  },
]

function pickPrompt(card: CapabilityCard, connectedProviders: Set<string>): string {
  // Prefer a prompt whose required provider is connected
  for (const prompt of card.prompts) {
    if (!prompt.providers) continue
    if (prompt.providers.some((p) => connectedProviders.has(p))) {
      return prompt.text
    }
  }
  // Fallback to a generic prompt (no provider requirement)
  const generic = card.prompts.find((p) => !p.providers)
  return generic?.text ?? card.prompts[0].text
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface LandingViewProps {
  greeting: { emoji: string; timeOfDay: string; name: string }
  input: string
  onInputChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onSend: () => void
  inputRef: RefObject<HTMLTextAreaElement | null>
  portfolios: Array<{ id: string; name: string; description?: string | null }>
  selectedPortfolioId: string
  showPortfolioMenu: boolean
  onSelectPortfolio: (id: string) => void
  onTogglePortfolioMenu: () => void
  portfolioMenuRef: RefObject<HTMLDivElement | null>
  onToggleMyActions: () => void
  actionItems: ActionItem[]
  integrations: Integration[]
  showIntegrations: boolean
  onToggleIntegrations: () => void
  onRefreshIntegrations: () => void
  integrationsButtonRef: RefObject<HTMLDivElement | null>
  onSendMessage: (text: string) => void
  attachedFile: File | null
  onAttachFile: (file: File | null) => void
}

export function LandingView({
  greeting,
  input,
  onInputChange,
  onKeyDown,
  onSend,
  inputRef,
  portfolios,
  selectedPortfolioId,
  showPortfolioMenu,
  onSelectPortfolio,
  onTogglePortfolioMenu,
  portfolioMenuRef,
  onToggleMyActions,
  actionItems,
  integrations,
  showIntegrations,
  onToggleIntegrations,
  onRefreshIntegrations,
  integrationsButtonRef,
  onSendMessage,
  attachedFile,
  onAttachFile,
}: LandingViewProps) {
  const pendingCount = actionItems.filter((item) => item.status === 'pending').length
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const connectedSet = new Set(
    integrations.filter((i) => i.status === 'connected').map((i) => i.provider),
  )
  // Stagger animation
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(t)
  }, [])

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* Header — no border below; the bg-shade differential between
       * bg-card (header) and the gradient body below provides separation. */}
      <div className="bg-card">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-soft ring-1 ring-accent/30">
              <TestOpsCommandLogo size={28} />
            </div>
            <div>
              <h1 className="font-mono text-base font-bold tracking-tight text-foreground">TestOps Command</h1>
              <p className="text-xs text-muted-foreground font-mono">// Orchestrate your entire test stack.</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <PortfolioDropdown
              portfolios={portfolios}
              selectedId={selectedPortfolioId}
              show={showPortfolioMenu}
              onSelect={onSelectPortfolio}
              onToggle={onTogglePortfolioMenu}
              menuRef={portfolioMenuRef}
            />
            <button
              onClick={onToggleMyActions}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border bg-card px-2.5 text-xs font-medium text-foreground/80 transition hover:bg-foreground/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ListTodo className="h-3.5 w-3.5 text-muted-foreground" />
              <span>My Actions</span>
              {pendingCount > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-white">
                  {pendingCount}
                </span>
              )}
            </button>
            <IntegrationsDropdown
              integrations={integrations}
              show={showIntegrations}
              onToggle={onToggleIntegrations}
              onRefresh={onRefreshIntegrations}
              buttonRef={integrationsButtonRef}
            />
          </div>
        </div>
      </div>

      {/* Centered content */}
      <div className="flex flex-1 items-center justify-center bg-gradient-to-b from-background via-card to-background" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <div className="w-full max-w-3xl px-8" style={{ marginTop: '10vh' }}>

          {/* Greeting */}
          <div
            className={clsx(
              'mb-8 text-left transition-all duration-700 ease-out',
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
            )}
          >
            <h2 className="mb-2 text-5xl font-light text-foreground">
              <span className="mr-3 text-4xl">{greeting.emoji}</span>
              <span>{greeting.timeOfDay}, </span>
              <span className="bg-gradient-to-r from-accent to-cyan-400 bg-clip-text font-medium text-transparent">
                {greeting.name}
              </span>
            </h2>
            <p className="text-base text-muted-foreground mt-2">
              Cross-reference your test runs, tickets, code, and docs &mdash; ask in plain English.
            </p>
          </div>

          {/* Input area — subtle card that lights up with cyan focus ring
           * when focused. shadow-sm in light only; dark canvases don't
           * render shadows well so we drop them via dark:shadow-none. */}
          <div
            className={clsx(
              'bg-card rounded-2xl border shadow-sm dark:shadow-none transition-all duration-300 ease-out delay-100',
              'focus-within:ring-2 focus-within:ring-ring focus-within:border-ring focus-within:shadow-md focus-within:shadow-accent/10',
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
            )}
          >
            {attachedFile && (
              <div className="mx-4 mt-4 flex items-center gap-2 px-3 py-2 bg-accent-soft border border-accent/30 rounded-lg">
                {(() => {
                  const Icon = getFileIcon(attachedFile.type)
                  return <Icon className="w-4 h-4 text-accent shrink-0" />
                })()}
                <span className="text-sm text-accent truncate flex-1">{attachedFile.name}</span>
                <span className="text-xs text-accent shrink-0">{formatSize(attachedFile.size)}</span>
                <button onClick={() => { onAttachFile(null); setFileError(null) }} className="p-0.5 hover:bg-accent/15 rounded transition shrink-0">
                  <X className="w-3.5 h-3.5 text-accent" />
                </button>
              </div>
            )}
            {fileError && <p className="mx-4 mt-2 text-xs text-destructive">{fileError}</p>}
            <div className="p-4">
              <textarea
                ref={inputRef as LegacyRef<HTMLTextAreaElement>}
                value={input}
                onChange={(e) => {
                  onInputChange(e.target.value)
                  if (inputRef.current) {
                    inputRef.current.style.height = 'auto'
                    inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`
                  }
                }}
                onKeyDown={onKeyDown}
                placeholder="What would you like to do?"
                className="w-full bg-transparent resize-none outline-none text-base text-foreground placeholder:text-subtle-foreground"
                rows={2}
                style={{ minHeight: '48px', maxHeight: '120px' }}
                autoFocus
              />
            </div>
            <div className="border-t border-subtle px-4 py-3 flex items-center justify-between">
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.txt,.csv,.json,.md,.xml,.html,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.log,.yaml,.yml,.rtf,.py,.js,.ts,.sql,.sh,.java,.go,.rb,.php,.css,.toml,.ini,.cfg,.env"
                  onChange={(e) => {
                    setFileError(null)
                    const file = e.target.files?.[0]
                    if (!file) return
                    if (file.size > 10 * 1024 * 1024) { setFileError('File must be under 10 MB'); return }
                    onAttachFile(file)
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 hover:bg-foreground/[0.06] rounded-lg transition"
                  title="Attach a file"
                >
                  <Paperclip className="w-4 h-4 text-subtle-foreground" />
                </button>
              </div>
              <button
                onClick={onSend}
                disabled={!input.trim() && !attachedFile}
                className={clsx(
                  'px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
                  (input.trim() || attachedFile)
                    ? 'bg-accent text-white hover:bg-accent/90 shadow-md shadow-accent/30 hover:shadow-lg hover:shadow-accent/40'
                    : 'bg-foreground/[0.06] text-subtle-foreground cursor-not-allowed',
                )}
              >
                <Send className="w-4 h-4" />
                Send
              </button>
            </div>
          </div>

          {/* Capability cards */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            {CAPABILITIES.map((card, idx) => {
              const Icon = card.icon
              const prompt = pickPrompt(card, connectedSet)
              return (
                <button
                  key={card.title}
                  onClick={() => onSendMessage(prompt)}
                  className={clsx(
                    'group text-left rounded-xl border bg-card px-4 py-3 transition-all duration-700 ease-out shadow-sm hover:shadow-md dark:shadow-none',
                    'hover:bg-foreground/[0.02]',
                    card.borderHover,
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
                  )}
                  style={{ transitionDelay: `${200 + idx * 100}ms` }}
                >
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <div className={clsx('inline-flex h-7 w-7 items-center justify-center rounded-lg shrink-0', card.iconBg)}>
                      <Icon className={clsx('w-3.5 h-3.5', card.iconColor)} />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground">{card.title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-2">{card.description}</p>
                  <p className="text-xs text-subtle-foreground italic group-hover:text-muted-foreground transition-colors">
                    &ldquo;{prompt}&rdquo;
                  </p>
                </button>
              )
            })}
          </div>

          <div className="mb-8" />
        </div>
      </div>
    </div>
  )
}
